import type {
    VirtualStructureEntryItem,
    ConversionAlgoConfig,
    MobileAlgoPluginInitializationArgs,
    ConversionDal,
    PluginContext,
    StructureIterators
} from '../../types'
import {createServerRequest} from './request'
import {CONTAINER_GROUP, CTX_SERVER, VIRTUAL_TAG} from './constants'
import type {Rect} from '@wix/document-services-types'
import type {GroupingResult} from './types'
import {getMeasurementsPointer, getStructurePointer} from '../../conversionDal/utils/pointer'

export const groupingAIResult = async (
    structureIterators: StructureIterators,
    conversionDal: ConversionDal,
    pageId: string,
    pluginInitArgs: MobileAlgoPluginInitializationArgs,
    contextHelper: PluginContext,
    config: ConversionAlgoConfig
): Promise<GroupingResult> => {
    const {readOnlyExtensionAPI, environmentContext} = pluginInitArgs
    const requestData = createServerRequest(
        structureIterators,
        conversionDal,
        pageId,
        readOnlyExtensionAPI,
        config,
        contextHelper
    )
    const sendRequest = contextHelper.get(CTX_SERVER)

    return await sendRequest(readOnlyExtensionAPI, environmentContext, requestData)
}

const calculateRealChildren = (children: VirtualStructureEntryItem[]): string[] =>
    children.flatMap(child => (child.componentType === VIRTUAL_TAG ? child.realChildren : [child.id]))

const calculateMeasurements = (children: VirtualStructureEntryItem[]): Rect => {
    const groupMeasurements = {left: Infinity, top: Infinity, right: 0, bottom: 0}

    children.forEach(({measurements}) => {
        groupMeasurements.left = Math.min(groupMeasurements.left, measurements.x)
        groupMeasurements.top = Math.min(groupMeasurements.top, measurements.y)
        groupMeasurements.right = Math.max(groupMeasurements.right, measurements.x + measurements.width)
        groupMeasurements.bottom = Math.max(groupMeasurements.bottom, measurements.y + measurements.height)
    })

    return {
        x: groupMeasurements.left,
        y: groupMeasurements.top,
        width: groupMeasurements.right - groupMeasurements.left,
        height: groupMeasurements.bottom - groupMeasurements.top
    }
}

const getMeasurements = (conversionDal: ConversionDal, group: GroupingResult) => {
    return conversionDal.get(getMeasurementsPointer({id: group.id}))
}

const getChildrenIdMap = (
    componentToChildrenGroupMap: Record<string, VirtualStructureEntryItem>,
    group: GroupingResult
) => {
    return componentToChildrenGroupMap[group.id].components!.map(
        childId => componentToChildrenGroupMap[childId]
    ) as VirtualStructureEntryItem[]
}

export const createVirtualGroupMap = (
    serverResponse: GroupingResult,
    conversionDal: ConversionDal
): Record<string, VirtualStructureEntryItem> => {
    const componentToChildrenGroupMap: Record<string, VirtualStructureEntryItem> = {}

    const scanGroupingResultAndBuildGroupsTree = (group: GroupingResult, parent: GroupingResult) => {
        if (group.id === parent.id) {
            return
        }

        if (group.tag !== 'Group') {
            const compStructure = conversionDal.get(getStructurePointer({id: group.id}))
            const compMeasurements = getMeasurements(conversionDal, group)
            componentToChildrenGroupMap[group.id] = {
                id: group.id,
                components: [],
                componentType: compStructure.componentType,
                measurements: compMeasurements,
                parentId: parent.id,
                realChildren: []
            }
            return
        }

        const childContainer = group.children!.find(child => child.id === group.id)?.id

        if (childContainer) {
            const compMeasurements = getMeasurements(conversionDal, group)
            componentToChildrenGroupMap[group.id] = {
                id: group.id,
                components: group.children?.map(child => child.id) ?? [],
                componentType: CONTAINER_GROUP,
                measurements: compMeasurements,
                parentId: parent.id
            } as VirtualStructureEntryItem
        } else {
            componentToChildrenGroupMap[group.id] = {
                id: group.id,
                components: group.children?.map(child => child.id) ?? [],
                componentType: VIRTUAL_TAG,
                parentId: parent.id
            } as VirtualStructureEntryItem
        }

        for (const child of group.children!) {
            scanGroupingResultAndBuildGroupsTree(child, group)
        }

        if (childContainer) {
            //remove duplicate children - the children is the container of the group
            componentToChildrenGroupMap[group.id].components = componentToChildrenGroupMap[group.id].components!.filter(
                child => child !== group.id
            )
            const children = getChildrenIdMap(componentToChildrenGroupMap, group)
            componentToChildrenGroupMap[group.id].realChildren = calculateRealChildren(children)
            return
        }

        if (componentToChildrenGroupMap[group.id].componentType === VIRTUAL_TAG) {
            const children = getChildrenIdMap(componentToChildrenGroupMap, group)
            componentToChildrenGroupMap[group.id].measurements = calculateMeasurements(children)
            componentToChildrenGroupMap[group.id].realChildren = calculateRealChildren(children)
            return
        }
    }

    scanGroupingResultAndBuildGroupsTree(serverResponse.children![0], serverResponse)

    return componentToChildrenGroupMap
}
